import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import BottomNav from '@/components/BottomNav';
import { PWAInstallBanner } from '@/components/PWAInstallBanner';
import SmartInsights from '@/components/SmartInsights';
import WeeklySummaryCard from '@/components/WeeklySummaryCard';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Wallet, Users, Bell, ArrowRight, Lock, TrendingUp,
  Calendar, Shield, PiggyBank, BarChart3, Flame, Clock,
  Target, Trophy, Zap, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summaryDismissed, setSummaryDismissed] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard`, {
        headers: getAuthHeaders()
      });
      setDashboard(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency', currency: 'NGN',
      minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(amount).replace('NGN', '\u20a6');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-NG', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const getTimeRemaining = (dateStr) => {
    if (!dateStr) return '';
    const now = new Date();
    const due = new Date(dateStr);
    const diff = due - now;
    if (diff <= 0) return 'Due now';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h left`;
    return `${hours}h left`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 pb-24">
        <div className="app-container px-4 pt-6">
          <div className="space-y-6">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-48 w-full rounded-2xl" />
            <Skeleton className="h-32 w-full rounded-2xl" />
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  const streak = dashboard?.streak || 0;
  const savProg = dashboard?.savings_progress || {};
  const contribStatus = dashboard?.contribution_status || [];

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="home-screen">
      <div className="app-container px-4 pt-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-slate-500 text-sm">Welcome back,</p>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="user-greeting">
              {user?.full_name?.split(' ')[0]}
            </h1>
          </div>
          <button 
            onClick={() => navigate('/activity')}
            className="relative p-3 rounded-full bg-white shadow-sm hover:shadow-md transition-shadow"
            data-testid="notifications-button"
          >
            <Bell size={20} className="text-slate-700" />
            {dashboard?.unread_notifications > 0 && (
              <span className="absolute top-2 right-2 w-2 h-2 bg-emerald-500 rounded-full pulse-dot" />
            )}
          </button>
        </div>

        {/* Streak + Progress Row */}
        {(streak > 0 || savProg.percentage > 0) && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            {/* Streak Card */}
            <Card className="glass-card overflow-hidden" data-testid="streak-card">
              <CardContent className="p-3 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Flame size={20} className={streak >= 3 ? 'text-orange-500' : 'text-slate-400'} />
                  <span className="text-2xl font-black text-slate-900">{streak}</span>
                </div>
                <p className="text-xs font-semibold text-slate-600">{streak === 1 ? 'Week Streak' : 'Week Streak'}</p>
                {streak >= 3 && <p className="text-[10px] text-orange-600 font-bold mt-1">Don't break it!</p>}
              </CardContent>
            </Card>

            {/* Savings Progress Card */}
            <Card className="glass-card overflow-hidden" data-testid="savings-progress-card">
              <CardContent className="p-3 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Target size={18} className="text-emerald-600" />
                  <span className="text-2xl font-black text-slate-900">{savProg.percentage || 0}%</span>
                </div>
                <p className="text-xs font-semibold text-slate-600">Savings Goal</p>
                {savProg.total_saved > 0 && (
                  <p className="text-[10px] text-emerald-600 font-bold mt-1">
                    {formatCurrency(savProg.total_saved)} saved
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Motivational Progress Bar */}
        {savProg.total_target > 0 && (
          <Card className="glass-card mb-4 border-l-4 border-l-emerald-500" data-testid="progress-motivation-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-slate-800">Your Savings Progress</p>
                <span className="text-xs font-bold text-emerald-600">{savProg.percentage}% complete</span>
              </div>
              <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden mb-2">
                <div 
                  className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-1000"
                  style={{ width: `${Math.min(savProg.percentage, 100)}%` }}
                />
              </div>
              <p className="text-xs text-slate-500">
                {formatCurrency(savProg.total_saved)} of {formatCurrency(savProg.total_target)} 
                {savProg.percentage >= 50 && savProg.percentage < 100 && ' — Keep going, you\'re past halfway!'}
                {savProg.percentage >= 100 && ' — Goal reached!'}
                {savProg.percentage < 50 && ' — Every contribution counts!'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Weekly Rewards Summary */}
        {!summaryDismissed && dashboard?.weekly_summary && (
          <div className="mb-4">
            <WeeklySummaryCard
              summary={dashboard.weekly_summary}
              onDismiss={() => setSummaryDismissed(true)}
            />
          </div>
        )}

        {/* Balance Card */}
        <Card className="balance-card mb-4" data-testid="balance-card">
          <div className="flex items-center gap-2 mb-1">
            <img 
              src="/icon-v2-192.png" 
              alt="AegisPay" 
              className="w-6 h-6 rounded"
            />
            <span className="text-sm text-white/80">Total Balance</span>
            <span className="demo-badge ml-auto">DEMO</span>
          </div>
          <h2 className="text-3xl font-bold mb-4" data-testid="total-balance">
            {formatCurrency((dashboard?.wallet?.available_balance || 0) + (dashboard?.wallet?.locked_balance || 0))}
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded-xl bg-[#2272C5]/30" data-testid="available-balance">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full bg-[#38B6CC]" />
                <span className="text-xs text-white/70">Available</span>
              </div>
              <p className="text-lg font-semibold">{formatCurrency(dashboard?.wallet?.available_balance || 0)}</p>
            </div>
            <div className="p-3 rounded-xl bg-amber-500/20" data-testid="locked-balance">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full bg-amber-400" />
                <span className="text-xs text-white/70">Locked</span>
              </div>
              <p className="text-lg font-semibold">{formatCurrency(dashboard?.wallet?.locked_balance || 0)}</p>
            </div>
          </div>
        </Card>

        {/* Group Contribution Status (FOMO + Social Pressure) */}
        {contribStatus.length > 0 && (
          <div className="space-y-2 mb-4">
            {contribStatus.map((cs) => (
              <Card 
                key={cs.group_id} 
                className="glass-card cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate(`/ajo/${cs.group_id}`)}
                data-testid={`contrib-status-${cs.group_id}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-slate-800 truncate flex-1">{cs.group_name}</p>
                    {cs.remaining > 0 ? (
                      <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full flex-shrink-0">
                        {cs.remaining} pending
                      </span>
                    ) : (
                      <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full flex-shrink-0">
                        All paid
                      </span>
                    )}
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${cs.remaining === 0 ? 'bg-emerald-500' : 'bg-[#2272C5]'}`}
                      style={{ width: `${(cs.paid / cs.total) * 100}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">
                    {cs.paid} of {cs.total} members have contributed this cycle
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Next Contribution Alert with Countdown */}
        {dashboard?.next_contribution && (
          <Card className="glass-card mb-4 border-l-4 border-l-[#2272C5]" data-testid="next-contribution-alert">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-xl bg-[#2272C5]/10">
                  <Calendar size={18} className="text-[#2272C5]" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">Next Contribution Due</p>
                  <p className="text-sm text-slate-500 mb-2">{dashboard.next_contribution.group_name}</p>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-lg font-bold text-[#2272C5]">
                      {formatCurrency(dashboard.next_contribution.amount)}
                    </span>
                    <div className="text-right">
                      <span className="text-xs text-slate-500 block">{formatDate(dashboard.next_contribution.due_date)}</span>
                      <span className="text-xs font-bold text-red-500 flex items-center gap-1 justify-end">
                        <Clock size={10} />{getTimeRemaining(dashboard.next_contribution.due_date)}
                      </span>
                    </div>
                  </div>
                  <div className="p-2 rounded-lg bg-amber-50 border border-amber-200">
                    <p className="text-xs text-amber-700 flex items-center gap-1">
                      <Shield size={12} />
                      <span><strong>Auto-Debit:</strong> Amount will be automatically deducted on contribution day</span>
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <Card className="glass-card cursor-pointer group-card" onClick={() => navigate('/ajo')} data-testid="active-groups-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-xl bg-indigo-100"><Users size={18} className="text-indigo-600" /></div>
                <ArrowRight size={16} className="text-slate-400" />
              </div>
              <p className="text-2xl font-bold text-slate-900">{dashboard?.active_groups || 0}</p>
              <p className="text-sm text-slate-500">Active Groups</p>
            </CardContent>
          </Card>

          <Card className="glass-card cursor-pointer group-card" onClick={() => navigate('/wallet')} data-testid="wallet-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-xl bg-amber-100"><Lock size={18} className="text-amber-600" /></div>
                <ArrowRight size={16} className="text-slate-400" />
              </div>
              <p className="text-2xl font-bold text-slate-900">{formatCurrency(dashboard?.wallet?.locked_balance || 0)}</p>
              <p className="text-sm text-slate-500">Locked Funds</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="space-y-3">
          <h3 className="font-semibold text-slate-900">Quick Actions</h3>
          
          <Button onClick={() => navigate('/ajo/create')} className="w-full h-14 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white justify-start px-4 btn-press" data-testid="create-group-button">
            <div className="p-2 rounded-xl bg-white/20 mr-3"><Users size={18} /></div>
            <div className="text-left"><p className="font-bold text-white">Create Ajo Group</p><p className="text-xs text-white/80">Start a new savings group</p></div>
            <ArrowRight size={18} className="ml-auto" />
          </Button>

          <Button onClick={() => navigate('/ajo')} variant="outline" className="w-full h-14 rounded-2xl border-2 border-[#2272C5] justify-start px-4 btn-press" data-testid="join-group-button">
            <div className="p-2 rounded-xl bg-[#2272C5]/10 mr-3"><TrendingUp size={18} className="text-[#2272C5]" /></div>
            <div className="text-left"><p className="font-medium text-[#0B1F3A]">Join Ajo Group</p><p className="text-xs text-slate-500">Browse available groups</p></div>
            <ArrowRight size={18} className="ml-auto text-slate-400" />
          </Button>

          <Button onClick={() => navigate('/savings')} variant="outline" className="w-full h-14 rounded-2xl border-2 border-[#38B6CC] justify-start px-4 btn-press" data-testid="personal-savings-button">
            <div className="p-2 rounded-xl bg-[#38B6CC]/10 mr-3"><PiggyBank size={18} className="text-[#38B6CC]" /></div>
            <div className="text-left"><p className="font-medium text-[#0B1F3A]">Personal Savings</p><p className="text-xs text-slate-500">Save alone at your pace</p></div>
            <ArrowRight size={18} className="ml-auto text-slate-400" />
          </Button>

          <Button onClick={() => navigate('/analytics')} variant="outline" className="w-full h-14 rounded-2xl border-2 border-violet-400 justify-start px-4 btn-press" data-testid="analytics-button">
            <div className="p-2 rounded-xl bg-violet-100 mr-3"><BarChart3 size={18} className="text-violet-600" /></div>
            <div className="text-left"><p className="font-medium text-[#0B1F3A]">Analytics</p><p className="text-xs text-slate-500">Track your financial insights</p></div>
            <ArrowRight size={18} className="ml-auto text-slate-400" />
          </Button>

          <Button onClick={() => navigate('/challenges')} variant="outline" className="w-full h-14 rounded-2xl border-2 border-orange-400 justify-start px-4 btn-press" data-testid="challenges-button">
            <div className="p-2 rounded-xl bg-orange-100 mr-3"><Flame size={18} className="text-orange-600" /></div>
            <div className="text-left"><p className="font-medium text-[#0B1F3A]">Savings Challenges</p><p className="text-xs text-slate-500">Build streaks, earn badges</p></div>
            <ArrowRight size={18} className="ml-auto text-slate-400" />
          </Button>

          <Button onClick={() => navigate('/futurelock')} variant="outline" className="w-full h-14 rounded-2xl border-2 border-amber-500 justify-start px-4 btn-press" data-testid="futurelock-button">
            <div className="p-2 rounded-xl bg-amber-100 mr-3"><Lock size={18} className="text-amber-600" /></div>
            <div className="text-left"><p className="font-medium text-[#0B1F3A]">FutureLock Plan</p><p className="text-xs text-slate-500">Lock savings, achieve goals</p></div>
            <ArrowRight size={18} className="ml-auto text-slate-400" />
          </Button>

          <Button onClick={() => navigate('/achievements')} variant="outline" className="w-full h-14 rounded-2xl border-2 border-purple-400 justify-start px-4 btn-press" data-testid="achievements-button">
            <div className="p-2 rounded-xl bg-purple-100 mr-3"><Trophy size={18} className="text-purple-600" /></div>
            <div className="text-left"><p className="font-medium text-[#0B1F3A]">Achievements</p><p className="text-xs text-slate-500">Badges, leaderboard, titles</p></div>
            <ArrowRight size={18} className="ml-auto text-slate-400" />
          </Button>

          <Button onClick={() => navigate('/rewards')} variant="outline" className="w-full h-14 rounded-2xl border-2 border-yellow-500 justify-start px-4 btn-press" data-testid="rewards-button">
            <div className="p-2 rounded-xl bg-yellow-100 mr-3"><Sparkles size={18} className="text-yellow-600" /></div>
            <div className="text-left"><p className="font-medium text-[#0B1F3A]">Financial Profile</p><p className="text-xs text-slate-500">Trust score, credibility, circles</p></div>
            <ArrowRight size={18} className="ml-auto text-slate-400" />
          </Button>
        </div>

        {/* Smart Insights */}
        <div className="mt-6">
          <SmartInsights />
        </div>

        {/* Security Info */}
        <div className="mt-6 p-4 rounded-2xl bg-[#0B1F3A] text-white">
          <div className="flex items-start gap-3">
            <Shield size={20} className="text-white mt-0.5" />
            <div>
              <p className="font-bold text-white">Your funds are protected</p>
              <p className="text-sm text-white mt-1">
                50% lock qualification deposit ensures commitment from all members. 
                Additional security holds apply for early payout positions.
              </p>
              <p className="text-xs text-white/50 mt-2">app.aegispay.app</p>
            </div>
          </div>
        </div>
      </div>

      <PWAInstallBanner />
      <BottomNav />
    </div>
  );
};

export default Home;
